// Create Property Modal - Mobile version using shared hook
import React from 'react';
import { View, Text, ScrollView, TouchableOpacity, ActivityIndicator, Modal, StyleSheet } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { useCreateProperty } from '@/hooks/admin/useCreateProperty';
import { KeyboardSafeView } from '@/components/common/KeyboardSafeView';
import { SVG_COMPONENTS } from '@/assets/admin';
import {
  PropertyGeneralInfoSection,
  PropertyPMSTypeSection,
  PropertyYardiSection,
  PropertyEntrataSection,
  PropertyCardConnectSection,
  PropertyDetailsSection,
  PropertyFeesSection,
} from './form-sections';

interface CreatePropertyModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const CreatePropertyModal: React.FC<CreatePropertyModalProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const {
    formData,
    errors,
    isLoading,
    handleInputChange,
    handleSubmit,
  } = useCreateProperty({ visible, onClose, onSuccess });

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet">
      <KeyboardSafeView isModal>
        <View style={styles.container}>
          {/* Header */}
          <View style={styles.header}>
          <Text style={styles.headerTitle}>Create Property</Text>
          <TouchableOpacity onPress={onClose} style={styles.closeButton}>
            <SVG_COMPONENTS.CANCEL_X width={24} height={24} color={ADMIN_COLORS.TEXT.PRIMARY} />
          </TouchableOpacity>
        </View>

        {/* Form */}
        <ScrollView style={styles.scrollView}>
          {/* General Information */}
          <PropertyGeneralInfoSection
            marketingName={formData.marketingName}
            externalId={formData.externalId}
            organizationId={formData.organizationId}
            nameError={errors.marketingName}
            externalIdError={errors.externalId}
            organizationIdError={errors.organizationId}
            onMarketingNameChange={(value) => handleInputChange('marketingName', value)}
            onExternalIdChange={(value) => handleInputChange('externalId', value)}
            onOrganizationIdChange={(value) => handleInputChange('organizationId', value)}
            disabled={isLoading}
          />

          {/* PMS Type Selection */}
          <PropertyPMSTypeSection
            pmsType={formData.pmsType}
            onPmsTypeChange={(value) => handleInputChange('pmsType', value)}
            disabled={isLoading}
          />

          {/* Yardi Configuration - Show only if Yardi is selected */}
          {formData.pmsType === 'yardi' && (
            <PropertyYardiSection
              yardiDomain={formData.yardiDomain}
              yardiUsername={formData.yardiUsername}
              yardiPassword={formData.yardiPassword}
              yardiServerName={formData.yardiServerName}
              yardiDatabase={formData.yardiDatabase}
              yardiBpInterfaceEntity={formData.yardiBpInterfaceEntity}
              yardiBpInterfaceLicense={formData.yardiBpInterfaceLicense}
              onYardiDomainChange={(value) => handleInputChange('yardiDomain', value)}
              onYardiUsernameChange={(value) => handleInputChange('yardiUsername', value)}
              onYardiPasswordChange={(value) => handleInputChange('yardiPassword', value)}
              onYardiServerNameChange={(value) => handleInputChange('yardiServerName', value)}
              onYardiDatabaseChange={(value) => handleInputChange('yardiDatabase', value)}
              onYardiBpInterfaceEntityChange={(value) => handleInputChange('yardiBpInterfaceEntity', value)}
              onYardiBpInterfaceLicenseChange={(value) => handleInputChange('yardiBpInterfaceLicense', value)}
              disabled={isLoading}
            />
          )}

          {/* Entrata Ledger Settings - Show only if Entrata is selected */}
          {formData.pmsType === 'entrata' && (
            <PropertyEntrataSection
              propertyCashAccountId={formData.propertyCashAccountId}
              accountsReceivableToResioAccountId={formData.accountsReceivableToResioAccountId}
              convenienceFeeAccountId={formData.convenienceFeeAccountId}
              onPropertyCashAccountIdChange={(value) => handleInputChange('propertyCashAccountId', value)}
              onAccountsReceivableChange={(value) => handleInputChange('accountsReceivableToResioAccountId', value)}
              onConvenienceFeeChange={(value) => handleInputChange('convenienceFeeAccountId', value)}
              disabled={isLoading}
            />
          )}

          {/* Card Connect Settings */}
          <PropertyCardConnectSection
            creditCardMerchantAccountId={formData.creditCardMerchantAccountId}
            achMerchantAccountId={formData.achMerchantAccountId}
            onCreditCardChange={(value) => handleInputChange('creditCardMerchantAccountId', value)}
            onAchChange={(value) => handleInputChange('achMerchantAccountId', value)}
            disabled={isLoading}
          />

          {/* Property Details */}
          <PropertyDetailsSection
            propertyEmailAddress={formData.propertyEmailAddress}
            propertyPhoneNumber={formData.propertyPhoneNumber}
            propertyUrl={formData.propertyUrl}
            propertyFaqUrl={formData.propertyFaqUrl}
            propertyResidentHandbook={formData.propertyResidentHandbook}
            propertyCbxFilePath=""
            emailError={errors.propertyEmailAddress}
            phoneError={errors.propertyPhoneNumber}
            urlError={errors.propertyUrl}
            onEmailChange={(value) => handleInputChange('propertyEmailAddress', value)}
            onPhoneChange={(value) => handleInputChange('propertyPhoneNumber', value)}
            onUrlChange={(value) => handleInputChange('propertyUrl', value)}
            onFaqUrlChange={(value) => handleInputChange('propertyFaqUrl', value)}
            onHandbookChange={(value) => handleInputChange('propertyResidentHandbook', value)}
            onCbxFilePathChange={() => {}}
            disabled={isLoading}
          />

          {/* Property Fees */}
          <PropertyFeesSection
            propertyNsfFee={formData.propertyNsfFee}
            propertyLateDayStart={formData.propertyLateDayStart}
            propertyLateDayFee={formData.propertyLateDayFee}
            propertyLateDailyFee={formData.propertyLateDailyFee}
            propertyMaxMonthlyFee={formData.propertyMaxMonthlyFee}
            nsfFeeError={errors.propertyNsfFee}
            lateDayFeeError={errors.propertyLateDayFee}
            lateDailyFeeError={errors.propertyLateDailyFee}
            maxMonthlyFeeError={errors.propertyMaxMonthlyFee}
            onNsfFeeChange={(value) => handleInputChange('propertyNsfFee', value)}
            onLateDayStartChange={(value) => handleInputChange('propertyLateDayStart', value)}
            onLateDayFeeChange={(value) => handleInputChange('propertyLateDayFee', value)}
            onLateDailyFeeChange={(value) => handleInputChange('propertyLateDailyFee', value)}
            onMaxMonthlyFeeChange={(value) => handleInputChange('propertyMaxMonthlyFee', value)}
            disabled={isLoading}
          />
        </ScrollView>

        {/* Footer Buttons */}
        <View style={styles.footer}>
          <TouchableOpacity style={styles.cancelButton} onPress={onClose}>
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={[styles.submitButton, isLoading && styles.buttonDisabled]}
            onPress={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <Text style={styles.submitButtonText}>Create</Text>
            )}
          </TouchableOpacity>
        </View>
      </View>
      </KeyboardSafeView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  closeButton: {
    padding: 8,
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: 20,
  },
  section: {
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
    marginBottom: 16,
  },
  field: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT.SECONDARY,
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    color: ADMIN_COLORS.TEXT.PRIMARY,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
  inputError: {
    borderColor: '#F87171',
  },
  errorText: {
    color: '#F87171',
    fontSize: 12,
    marginTop: 4,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    marginBottom: 12,
    borderTopColor: ADMIN_COLORS.BORDER,
    gap: 12,
  },
  cancelButton: {
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
  },
  cancelButtonText: {
    color: ADMIN_COLORS.TEXT.PRIMARY,
    fontSize: 14,
    fontWeight: '600',
  },
  submitButton: {
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: ADMIN_COLORS.BUTTON.ADD_BUTTON_BACKGROUND,
    minWidth: 120,
  },
  submitButtonText: {
    color: ADMIN_COLORS.BUTTON.PRIMARY_TEXT,
    fontSize: 14,
    fontWeight: '600',
  },
  buttonDisabled: {
    opacity: 0.5,
  },
});

